import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Users, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchTownhallHub, subscribeTownhallInspiration } from "@/lib/townhall/townhall-service";
import { InspirationDashboard } from "@/components/townhall/InspirationDashboard";

/**
 * VedaPath Townhall — global inspiration hub for collective chanting.
 */
export function TownhallPage() {
  const [hub, setHub] = useState(null);
  const [inspiration, setInspiration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTownhallHub()
      .then((data) => {
        setHub(data);
        setInspiration(data.inspiration);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const unsub = subscribeTownhallInspiration(setInspiration);
    return unsub;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const chants = hub?.chants ?? [];

  return (
    <section className="min-h-screen py-24 px-4 noise" data-testid="townhall-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-6 h-6 text-amber-500" />
          <h1 className="font-heading text-2xl md:text-3xl">VedaPath Townhall</h1>
          <Badge variant="secondary" className="border-amber-200">
            <Users className="w-3 h-3 mr-1" /> Global saṅga
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Chant together with sādhakas worldwide. Begin synced chanting with scrolling Sanskrit text and translation,
          or add offline repetitions to inspire the collective tally.
        </p>

        {error && <p className="text-destructive text-sm mb-4">{error}</p>}

        <InspirationDashboard inspiration={inspiration} personalCount={0} />

        <h2 className="font-heading text-lg mb-4">Choose a chant</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {chants.map((c) => {
            const row = inspiration?.by_chant?.find((b) => b.chant_id === c.id);
            return (
              <Card key={c.id} className="bg-white/90 shadow-card border-amber-100" data-testid={`townhall-chant-${c.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-heading">{c.title}</CardTitle>
                  <p className="text-xs text-muted-foreground capitalize">
                    {c.type?.replace("_", " ")} · {Math.round(c.duration_seconds / 60)} min
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {(row?.cycles_today ?? 0).toLocaleString()} community cycles today
                  </p>
                  <Button size="sm" className="w-full" asChild>
                    <Link to={`/townhall/${c.id}`}>Enter Townhall</Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 justify-center mt-8">
          <Button variant="outline" asChild>
            <Link to="/community-hall">Community Hall (LIVE)</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/prayer-hall">← Dhyāna Mandir</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
